import React, { useEffect, useMemo, useState } from 'react';
import { DataTable, TableColumn } from '../../../../../ui';
import { NameEmailAvatar } from '../../../../../ui/components/Common/NameEmailAvatar';
import { toast } from '../../../../../ui/toast';
import { UsageMeter } from '../../../../../ui/usage_meter/UsageMeter';
import {
  agenticErrorMessage,
  useGetAgenticApplicantsQuery,
  useGetAgenticAssessmentQuery,
  useInviteAgenticApplicantsMutation,
} from '../../../../../utils/api_service/agenticAssessmentApi';
import {
  AgenticApplicantFilter,
  AgenticApplicantRow,
  AgenticInviteResponse,
} from '../../../../../utils/types/agentic_assessment_types';
import { getFeatureCredits } from '../../../../../utils/api_service/credits_api';
import {
  AGENTIC_ASSESSMENT_INTERVIEW,
  FeatureCredits,
  creditTone,
  formatResetDate,
} from '../../../../../utils/types/credits_types';
import { APPLICANT_FILTERS, STATUS_CHIP, formatDate, fullName } from '../constants';

interface CandidatesPanelProps {
  jobOpeningId: string;
  onGoToSetup: () => void;
  onOpenReport: (sessionId: string) => void;
}

const PAGE_SIZE = 20;
// Sessions that are not finished can be sent again (the old link stops working).
const RESENDABLE: AgenticApplicantFilter[] = ['Invite Sent', 'In Progress', 'Expired'];
const IN_STAGE = 'Agentic-Assessment';

const inStage = (row: AgenticApplicantRow) => !row.applicantStatus || row.applicantStatus === IN_STAGE;

const stageLabel = (status?: string) => {
  if (!status || status === IN_STAGE) return 'Agentic Assessment';
  return status === 'Unsuccessful-Pending' ? 'Unsuccessful' : status;
};

export const CandidatesPanel: React.FC<CandidatesPanelProps> = ({ jobOpeningId, onGoToSetup, onOpenReport }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState<AgenticApplicantFilter | ''>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lastInvite, setLastInvite] = useState<AgenticInviteResponse | null>(null);
  const [credits, setCredits] = useState<FeatureCredits | null>(null);

  // Read on open (the count also settles invites that expired since); refreshed by each invite.
  useEffect(() => {
    getFeatureCredits(AGENTIC_ASSESSMENT_INTERVIEW)
      .then(async (res) => { if (res.ok) setCredits(await res.json()); })
      .catch(() => setCredits(null));
  }, [jobOpeningId]);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: assessment } = useGetAgenticAssessmentQuery(jobOpeningId);
  const { data, isFetching } = useGetAgenticApplicantsQuery(
    { jobOpeningId, page, limit: PAGE_SIZE, search: debouncedSearch, status },
    { refetchOnMountOrArgChange: true },
  );
  const [invite, { isLoading: isInviting }] = useInviteAgenticApplicantsMutation();

  const rows = data?.applicants ?? [];
  const total = data?.totalCount ?? 0;
  const approved = assessment?.status === 'Approved';
  // Invite/Resend only for candidates still in the stage -- the backend refuses the rest.
  const selectable = (row: AgenticApplicantRow) => row.assessmentStatus !== 'Completed' && inStage(row);
  const selectableRows = rows.filter(selectable);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.applicantId));
  const someSelected = selected.size > 0;

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableRows.map((r) => r.applicantId)));

  const sendInvites = async (applicantIds: string[]) => {
    try {
      const result = await invite({ jobOpeningId, applicantIds }).unwrap();
      setLastInvite(result);
      if (result.credits) setCredits(result.credits);
      setSelected(new Set());
      const sent = result.invited.length;
      if (sent) toast.success(`Sent ${sent} invite${sent === 1 ? '' : 's'}.`);
      if (result.skipped.length) toast.error(`${result.skipped.length} not sent. See the details above the table.`);
    } catch (e) {
      toast.error(agenticErrorMessage(e, 'Could not send the invites.'));
    }
  };

  const columns: TableColumn<AgenticApplicantRow>[] = useMemo(() => [
    {
      key: 'candidate',
      label: 'CANDIDATE',
      width: '24%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <NameEmailAvatar
            name={fullName(row.firstName, row.lastName)}
            email={row.email || ''}
            avatar={undefined}
            avatarWidth='32px'
            avatarHeight='32px'
            nameFontSize='13px'
            emailFontSize='11px'
        />
      ),
    },
    {
      key: 'assessmentStatus',
      label: 'INTERVIEW',
      width: '14%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CHIP[row.assessmentStatus]}`}>
          { row.assessmentStatus === 'Assigned' ? 'Not invited' : row.assessmentStatus }
        </span>
      ),
    },
    {
      key: 'applicantStatus',
      label: 'STAGE',
      width: '12%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <span className={`text-xs ${inStage(row) ? 'text-slate-500' : 'font-semibold text-indigo-700'}`}>
          { stageLabel(row.applicantStatus) }
        </span>
      ),
    },
    {
      key: 'progress',
      label: 'PROGRESS',
      width: '12%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <span className='text-sm tabular-nums text-slate-600'>
          { row.sessionId ? `${row.questionsAsked ?? 0} questions` : '—' }
        </span>
      ),
    },
    {
      key: 'overallFit',
      label: 'FIT SCORE',
      width: '10%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <span className='text-sm font-semibold tabular-nums text-slate-900'>
          { row.overallFit != null ? `${row.overallFit}%` : '—' }
        </span>
      ),
    },
    {
      key: 'dates',
      label: 'SENT / DEADLINE',
      width: '16%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <span className='text-xs text-slate-500'>
          { row.sessionId ? `${formatDate(row.sentAt)} / ${formatDate(row.expiresAt)}` : '—' }
        </span>
      ),
    },
    {
      key: 'action',
      label: '',
      width: '12%',
      align: 'left',
      disableSort: true,
      render: (row) => {
        if (row.assessmentStatus === 'Completed' && row.sessionId) {
          return (
            <button type='button' onClick={() => onOpenReport(row.sessionId as string)}
                className='text-xs font-semibold text-emerald-700 hover:text-emerald-800'>
              View report
            </button>
          );
        }
        const label = row.assessmentStatus === 'Assigned' ? 'Invite' : RESENDABLE.includes(row.assessmentStatus) ? 'Resend' : '';
        const moved = !inStage(row);
        return label ? (
          <button type='button' disabled={!approved || isInviting || moved} onClick={() => sendInvites([row.applicantId])}
              title={moved ? `Moved to ${stageLabel(row.applicantStatus)}. Move them back to Agentic Assessment to ${label.toLowerCase()}.` : undefined}
              className='rounded-lg border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40'>
            { label }
          </button>
        ) : <span />;
      },
    },
  ], [approved, isInviting, onOpenReport]);

  const nameFor = (id: string) => {
    const row = rows.find((r) => r.applicantId === id);
    return row ? fullName(row.firstName, row.lastName) : id;
  };

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <h3 className='text-base font-semibold text-slate-900'>Candidates</h3>
        <p className='text-sm text-slate-500'>
          Candidates you move to <span className='font-semibold'>Agentic Assessment</span> in Applicant Analysis appear here,
          and stay listed once invited even if you move them on (see Stage).
          Invites are valid for 7 days to start; there is no time limit once they begin.
        </p>
      </div>

      { credits && (
        <div className='rounded-xl border border-slate-200 bg-white px-4 py-3' data-testid='agentic-credits'>
          { credits.unlimited ? (
            <p className='text-sm text-slate-600'>
              <span className='font-semibold text-slate-900'>{ credits.used }</span> interview{ credits.used === 1 ? '' : 's' } sent this month.
            </p>
          ) : (
            <UsageMeter
                used={credits.used}
                limit={credits.monthlyLimit}
                tone={creditTone(credits)}
                leftLabel={`${credits.remaining} of ${credits.monthlyLimit} interviews left this month`}
                rightLabel={`Resets ${formatResetDate(credits)}`}
            />
          ) }
          <p className='mt-1 text-xs text-slate-500'>
            Each invite uses one interview. Invites that expire unstarted, or are replaced before they are finished, give it back.
          </p>
        </div>
      ) }

      { !approved && (
        <div className='flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3'>
          <p className='text-sm text-sky-800'>Approve the blueprint before inviting candidates.</p>
          <button type='button' onClick={onGoToSetup} className='text-sm font-semibold text-sky-800 hover:underline'>Go to setup</button>
        </div>
      ) }

      { lastInvite && lastInvite.skipped.length > 0 && (
        <div className='rounded-xl border border-amber-200 bg-amber-50 px-4 py-3'>
          <div className='flex items-start justify-between gap-3'>
            <p className='text-sm font-semibold text-amber-800'>Not sent to { lastInvite.skipped.length }:</p>
            <button type='button' className='text-xs text-amber-700 hover:underline' onClick={() => setLastInvite(null)}>Dismiss</button>
          </div>
          <ul className='mt-1 list-disc pl-5 text-sm text-amber-800'>
            { lastInvite.skipped.map((s) => <li key={s.applicantId}>{ nameFor(s.applicantId) }: { s.reason }</li>) }
          </ul>
        </div>
      ) }
      { lastInvite && lastInvite.invited.some((i) => !i.emailSent) && (
        <div className='rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          The invite was created but the email failed for { lastInvite.invited.filter((i) => !i.emailSent).map((i) => nameFor(i.applicantId)).join(', ') }.
          Use Resend to try again.
        </div>
      ) }

      <div className='flex flex-wrap items-center gap-2'>
        <input
            className='w-64 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40'
            placeholder='Search name or email'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
        />
        <div className='flex flex-wrap gap-1'>
          { (['', ...APPLICANT_FILTERS] as (AgenticApplicantFilter | '')[]).map((f) => (
            <button
                key={f || 'all'}
                type='button'
                onClick={() => { setStatus(f); setPage(1); setSelected(new Set()); }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  status === f ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
            >
              { f === '' ? 'All' : f === 'Assigned' ? 'Not invited' : f }
            </button>
          )) }
        </div>
      </div>

      { someSelected && (
        <div className='flex items-center gap-3 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-2'>
          <span className='text-sm font-semibold text-emerald-800'>{ selected.size } selected</span>
          <button
              type='button'
              disabled={!approved || isInviting}
              onClick={() => sendInvites(Array.from(selected))}
              className='rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50'
          >
            { isInviting ? 'Sending…' : `Send invites (${selected.size})` }
          </button>
          <button type='button' onClick={() => setSelected(new Set())} className='ml-auto text-xs text-slate-500 hover:text-slate-700'>
            Clear
          </button>
        </div>
      ) }

      { /* A leaked global `td { text-align: center !important }` beats the table's own alignment. */ }
      <div className='[&_td]:!text-left'>
        <DataTable<AgenticApplicantRow>
            isLoading={isFetching && !data}
            columns={columns}
            data={rows}
            rowsPerPage={PAGE_SIZE}
            currentPage={page}
            totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
            totalRecords={total}
            onPageChange={setPage}
            hasCheckBox
            onSelectAll={toggleAll}
            onSelectRow={(_i, row) => selectable(row) && toggle(row.applicantId)}
            isRowSelected={(_i, row) => selected.has(row.applicantId)}
            isAllSelected={allSelected}
            isIndeterminate={someSelected && !allSelected}
        />
      </div>
      { !isFetching && total === 0 && (
        <p className='text-center text-sm text-slate-500'>
          No candidates here yet. In Applicant Analysis, select candidates and choose “Move selected to Agentic Assessment”.
        </p>
      ) }
    </div>
  );
};
